import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';

const WS_BASE = process.env.REACT_APP_WS_API_BASE || 'ws://localhost:8081';

const decodePixels = (encoded) => {
  if (!encoded) return new Uint8Array();
  const binary = window.atob(encoded);
  const result = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) result[index] = binary.charCodeAt(index);
  return result;
};

const withCooldownReceiptTime = (value) => ({ ...value, cooldown_received_at: Date.now() });

export const usePixelBattle = (onPixelUpdate, leaderboardType = 0) => {
  const [event, setEvent] = useState(null);
  const [palette, setPalette] = useState([]);
  const [pixels, setPixels] = useState(new Uint8Array());
  const [leaderboard, setLeaderboard] = useState([]);
  const [onlineCount, setOnlineCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const eventRef = useRef(null);
  const onPixelUpdateRef = useRef(onPixelUpdate);
  const canvasID = event?.canvas?.id;

  useEffect(() => { eventRef.current = event; }, [event]);
  useEffect(() => { onPixelUpdateRef.current = onPixelUpdate; }, [onPixelUpdate]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    const [eventResult, paletteResult, leaderboardResult] = await Promise.allSettled([
      api('/pixel-battle/event'),
      api('/pixel-battle/palette'),
      api(`/pixel-battle/leaderboard?type=${leaderboardType}`),
    ]);
    if (eventResult.status === 'fulfilled') {
      setEvent(withCooldownReceiptTime(eventResult.value));
      setPixels(decodePixels(eventResult.value?.snapshot?.pixels));
      setError(null);
    } else if (eventResult.reason?.status === 404) {
      setEvent(null);
      setError(null);
    } else {
      setError(eventResult.reason?.message || 'Не удалось загрузить событие');
    }
    if (paletteResult.status === 'fulfilled') setPalette(paletteResult.value || []);
    if (leaderboardResult.status === 'fulfilled') setLeaderboard(leaderboardResult.value || []);
    setIsLoading(false);
  }, [leaderboardType]);

  useEffect(() => {
    setLeaderboard([]);
    load({ silent: true });
  }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!canvasID) return undefined;
    let socket;
    let reconnectTimer;
    let closed = false;
    const connect = () => {
      socket = new WebSocket(`${WS_BASE}/pixel-battle/ws`);
      socket.onmessage = ({ data }) => {
        try {
          const update = JSON.parse(data);
          if (update.type === 'snapshot') {
            setPixels(decodePixels(update.snapshot?.pixels));
          } else if (update.type === 'pixel') {
            const currentCanvas = eventRef.current?.canvas;
            const isValidPixel = currentCanvas && Number.isInteger(update.pixel?.x) && Number.isInteger(update.pixel?.y)
              && update.pixel.x >= 0 && update.pixel.y >= 0 && update.pixel.x < currentCanvas.width && update.pixel.y < currentCanvas.height;
            if (isValidPixel) onPixelUpdateRef.current?.(update.pixel);
            setPixels((current) => {
              const width = eventRef.current?.canvas?.width;
              if (!width) return current;
              const index = update.pixel.y * width + update.pixel.x;
              if (index < 0 || index >= current.length) return current;
              const next = current.slice();
              next[index] = update.pixel.color;
              return next;
            });
          }
        } catch (_) { /* polling restores malformed or missed updates */ }
      };
      socket.onclose = () => { if (!closed) reconnectTimer = window.setTimeout(connect, 2000); };
    };
    connect();
    return () => { closed = true; window.clearTimeout(reconnectTimer); socket?.close(); };
  }, [canvasID]);

  useEffect(() => {
    if (!canvasID) { setOnlineCount(null); return undefined; }
    let socket;
    let reconnectTimer;
    let closed = false;
    const connect = () => {
      socket = new WebSocket(`${WS_BASE}/pixel-battle/online/ws`);
      socket.onmessage = ({ data }) => {
        try {
          const update = JSON.parse(data);
          if (update.type === 'online' && Number.isInteger(update.online_count) && update.online_count >= 0) setOnlineCount(update.online_count);
        } catch (_) { /* the next server update restores the value */ }
      };
      socket.onclose = () => {
        if (!closed) {
          setOnlineCount(null);
          reconnectTimer = window.setTimeout(connect, 2000);
        }
      };
    };
    connect();
    return () => { closed = true; window.clearTimeout(reconnectTimer); socket?.close(); };
  }, [canvasID]);

  const joinTeam = useCallback(async (captainUserId) => {
    await api('/pixel-battle/team', { method: 'POST', body: JSON.stringify({ captain_user_id: captainUserId }) });
    await load({ silent: true });
  }, [load]);

  const placePixel = useCallback(async (x, y, color) => {
    const oldCanvas = eventRef.current?.canvas;
    const result = await api('/pixel-battle/pixel', { method: 'POST', body: JSON.stringify({ x, y, color }) });
    setEvent((current) => ({
      ...current,
      canvas: result.canvas,
      next_place_at: result.next_place_at,
      cooldown_remaining_seconds: result.cooldown_remaining_seconds,
      cooldown_received_at: Date.now(),
    }));
    setPixels((current) => {
      const oldWidth = oldCanvas?.width || result.canvas.width;
      const oldHeight = oldCanvas?.height || result.canvas.height;
      const expanded = oldWidth !== result.canvas.width || oldHeight !== result.canvas.height;
      const next = expanded ? new Uint8Array(result.canvas.width * result.canvas.height) : current.slice();
      if (expanded) {
        for (let row = 0; row < oldHeight; row += 1) next.set(current.subarray(row * oldWidth, (row + 1) * oldWidth), row * result.canvas.width);
      }
      next[y * result.canvas.width + x] = color;
      return next;
    });
    return result;
  }, []);

  return useMemo(() => ({ event, palette, pixels, leaderboard, onlineCount, isLoading, error, joinTeam, placePixel, reload: load }), [event, palette, pixels, leaderboard, onlineCount, isLoading, error, joinTeam, placePixel, load]);
};
